import { useRef, useEffect } from 'react';
import { useInView, useReducedMotion, useIsTouch } from '@hooks/useScroll';
import { ImageIcon } from 'lucide-react';
import { useSiteImages } from '@/lib/SiteImagesContext';
import { useSiteText } from '@/lib/SiteTextContext';
import { MagneticButton } from './MagneticButton';

export function LakeSection() {
  const { t } = useSiteText();
  const { img } = useSiteImages();
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const { ref, isInView } = useInView();
  const reduced = useReducedMotion();
  const isTouch = useIsTouch();
  // Desktop: use the native `background-attachment: fixed` (compositor-driven,
  // zero JS) so dragging the scrollbar is perfectly smooth (no jitter).
  // Touch/mobile: native fixed is broken there, so we pin via transform.
  const useNativeFixed = !isTouch && !reduced;

  useEffect(() => {
    if (useNativeFixed) return; // nothing to do, CSS handles it
    const el = sectionRef.current;
    const bg = bgRef.current;
    if (!el || !bg) return;

    let ticking = false;
    const update = () => {
      const rect = el.getBoundingClientRect();
      bg.style.transform = `translate3d(0, ${-rect.top}px, 0)`;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [useNativeFixed]);

  return (
    <section
      id="lake"
      ref={sectionRef}
      className="py-20 md:py-32 relative overflow-hidden"
    >
      {/* Frozen-background parallax.
          Desktop: native background-attachment:fixed (compositor, no jitter).
          Touch/reduced: JS transform pin (no native fixed support there). */}
      <div
        ref={bgRef}
        className={`absolute left-0 w-full bg-cover bg-center${useNativeFixed ? ' lake-fixed' : ''}`}
        style={{
          top: 0,
          height: '100vh',
          backgroundImage: `url('${img('lakeBg')}')`,
          transform: useNativeFixed ? 'none' : 'translate3d(0, 0, 0)',
          willChange: useNativeFixed ? 'auto' : 'transform',
        }}
      />
      <div className="absolute inset-0 bg-ocean-blue/65" />
      <div className="absolute inset-0 wave-overlay opacity-25" />
      <div
        ref={ref}
        className={`relative z-10 container mx-auto px-6 md:px-12 lg:px-24 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-3xl mx-auto text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-6">
            <ImageIcon className="w-6 h-6" />
            <span className="text-sm tracking-[0.3em] uppercase font-medium">
              {t('lake.label')}
            </span>
          </div>

          <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight mb-8 text-wave">
            {t('lake.title')}
          </h2>

          <p className="text-lg text-misty-blue/80 font-light leading-relaxed mb-10 max-w-2xl mx-auto">
            {t('lake.text')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <MagneticButton
              onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-shimmer inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent-gold text-ocean-blue font-bold hover:bg-accent-gold-dark hover:scale-105 shadow-lg shadow-accent-gold/30 will-change-transform"
            >
              {t('lake.btnGallery')}
            </MagneticButton>
            <MagneticButton
              onClick={() => document.getElementById('news-carousel')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-white/30 hover:border-white/60 hover:bg-white/10 backdrop-blur-sm will-change-transform"
              strength={0.25}
            >
              {t('lake.btnNews')}
            </MagneticButton>
          </div>
        </div>
      </div>
    </section>
  );
}
